import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../constants";
import { usePermitsList } from "./usePermitsList";
import {
  useDroughtProductionData,
  useAvailableYears,
} from "./useDroughtProductionData";
import DroughtProductionChart from "./DroughtProductionChart";

export default function DroughtProductionCutback() {
  const [selectedPermitId, setSelectedPermitId] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const {
    data: permits,
    isLoading: permitsLoading,
    isError: permitsError,
  } = usePermitsList();

  const {
    data: availableYears,
    isLoading: yearsLoading,
    isError: yearsError,
  } = useAvailableYears(selectedPermitId);

  const {
    monthlyData,
    yearSummary,
    isLoading: dataLoading,
    isError: dataError,
  } = useDroughtProductionData(selectedPermitId, selectedYear);

  const handlePermitChange = (event) => {
    const permitId = event.target.value;
    setSelectedPermitId(permitId);
    setSelectedYear(""); // Reset year when permit changes
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  if (permitsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (permitsError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load permits data.
      </Alert>
    );
  }

  const selectedPermit = permits?.find(
    (p) => p.permit_ndx.toString() === selectedPermitId
  );

  return (
    <Box>
      <Helmet title="Drought Production Cutback & Settlement" />

      <Typography variant="h3" gutterBottom display="inline">
        Production Cutback & Settlement
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Production Cutback & Settlement</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      {/* Selection Panel */}
      <Paper sx={{ p: 4, mb: 4, border: "1px solid #e0e0e0" }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
        >
          Report Parameters
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Choose a water permit and reporting year to generate the production
          cutback analysis. Only permits with available production data will
          appear in the selection.
        </Typography>

        <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Select Permit</InputLabel>
            <Select
              value={selectedPermitId}
              onChange={handlePermitChange}
              label="Select Permit"
              variant="outlined"
            >
              <MenuItem value="">
                <em>Choose a permit...</em>
              </MenuItem>
              {permits?.map((permit) => (
                <MenuItem
                  key={permit.permit_ndx}
                  value={permit.permit_ndx.toString()}
                >
                  #{permit.permit_ndx} -{" "}
                  {permit.permit_nickname || "No nickname"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedPermitId && (
            <>
              {yearsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 200,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading years...
                  </Typography>
                </Box>
              ) : yearsError ? (
                <Alert severity="error" sx={{ minWidth: 200 }}>
                  Failed to load available years
                </Alert>
              ) : availableYears && availableYears.length > 0 ? (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Select Year</InputLabel>
                  <Select
                    value={selectedYear}
                    onChange={handleYearChange}
                    label="Select Year"
                    variant="outlined"
                  >
                    <MenuItem value="">
                      <em>Choose a year...</em>
                    </MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year.toString()}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Alert severity="warning" sx={{ minWidth: 200 }}>
                  No production data available for this permit
                </Alert>
              )}
            </>
          )}
        </Box>

        {dataError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load production data for the selected permit and year.
          </Alert>
        )}
      </Paper>

      {selectedPermitId && selectedYear && (
        <>
          {dataLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : monthlyData && yearSummary && selectedPermit ? (
            <DroughtProductionChart
              permit={selectedPermit}
              year={selectedYear}
              monthlyData={monthlyData}
              yearSummary={yearSummary}
            />
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>
              No production data found for the selected permit and year.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
